<template>
  <div>
    <CRow>
      <CCol cols="12">
        <CCard>
          <CCardHeader>
            <strong>地域管理</strong>
            <div class="float-right">
              <CButton color="primary" size="sm" @click="openAddModal">添加地域</CButton>
              <CButton color="secondary" size="sm" class="ml-1" @click="loadList">刷新</CButton>
            </div>
          </CCardHeader>
          <CCardBody>
            <CDataTable
              :items="regions"
              :fields="fields"
              :loading="loading"
              hover
              striped
              outlined
              size="sm"
            >
              <template #region_id="{ item }">
                <td>
                  <CBadge color="info">{{ item.region_id }}</CBadge>
                </td>
              </template>
              <template #router_count="{ item }">
                <td>
                  <CBadge :color="item.router_count > 0 ? 'success' : 'secondary'">
                    {{ item.router_count }} 台路由器
                  </CBadge>
                </td>
              </template>
              <template #actions="{ item }">
                <td>
                  <CButtonGroup>
                    <CButton color="success" size="sm" @click="openRoutersModal(item)">路由器</CButton>
                    <CButton color="info" size="sm" @click="openEditModal(item)">编辑</CButton>
                    <CButton color="danger" size="sm" :disabled="item.router_count > 0" @click="confirmDelete(item)">删除</CButton>
                  </CButtonGroup>
                </td>
              </template>
            </CDataTable>
            <CAlert v-if="!loading && regions.length === 0" color="info" class="mb-0">
              暂无地域，点击右上角「添加地域」创建。地域可用于 Link IP 网段池归属和路由器分组筛选。
            </CAlert>
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>

    <!-- 添加/编辑 Modal -->
    <CModal :show.sync="modal.show" :title="modal.isEdit ? '编辑地域' : '添加地域'" :color="modal.isEdit ? 'info' : 'primary'">
      <CForm>
        <CInput
          label="地域名称"
          v-model="modal.form.name"
          placeholder="如 深圳 / 上海 / 广州"
          :is-valid="modalValidated ? !!modal.form.name : null"
          invalid-feedback="名称不能为空"
        />
        <CInput
          label="地域编号（纯数字，唯一）"
          v-model="modal.form.region_id"
          placeholder="如 1"
          :is-valid="modalValidated ? (/^\d+$/.test(modal.form.region_id) ? null : false) : null"
          invalid-feedback="必须为纯数字"
        />
        <CInput
          label="代码（可选）"
          v-model="modal.form.code"
          placeholder="如 sz / sh / gz"
        />
        <CTextarea
          label="备注（可选）"
          v-model="modal.form.remark"
          :rows="2"
          placeholder="备注信息"
        />
      </CForm>
      <template #footer>
        <CButtonGroup>
          <CButton color="secondary" @click="modal.show = false">取消</CButton>
          <CButton color="success" :disabled="modal.loading" @click="submit">{{ modal.loading ? '保存中...' : '保存' }}</CButton>
        </CButtonGroup>
      </template>
    </CModal>

    <!-- 管理地域路由器 Modal -->
    <CModal :show.sync="routersModal.show" :title="'管理路由器 - ' + (routersModal.region ? routersModal.region.name : '')" color="success" size="lg">
      <CAlert color="info" fade>
        勾选属于该地域的路由器后保存。已属于其他地域的路由器勾选后会被移入本地域；取消勾选则移出。
      </CAlert>
      <div v-if="routersModal.loading" class="text-center my-3">
        <CSpinner size="sm" />
      </div>
      <template v-else>
        <div style="max-height: 400px; overflow-y: auto;">
          <table class="table table-sm table-hover mb-0">
            <thead>
              <tr>
                <th style="width: 40px;"></th>
                <th>路由器</th>
                <th>位置</th>
                <th>当前地域</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="r in routersModal.list" :key="r.id">
                <td>
                  <input type="checkbox" :value="r.id" v-model="routersModal.selected" />
                </td>
                <td>
                  <strong>{{ r.name }}</strong>
                  <br />
                  <small class="text-muted">{{ r.host }}</small>
                </td>
                <td>{{ r.location || '-' }}</td>
                <td>
                  <CBadge v-if="r.region_id && !r.in_this_region && regionName(r.region_id)" color="warning">
                    {{ regionName(r.region_id) }}
                  </CBadge>
                  <template v-else-if="r.in_this_region">
                    <CBadge color="success">本地域</CBadge>
                  </template>
                  <template v-else>-</template>
                </td>
              </tr>
            </tbody>
          </table>
          <CAlert v-if="routersModal.list.length === 0" color="secondary" class="m-3">
            系统中还没有路由器。
          </CAlert>
        </div>
      </template>
      <template #footer>
        <CButtonGroup>
          <CButton color="secondary" @click="routersModal.show = false">取消</CButton>
          <CButton color="success" :disabled="routersModal.loading" @click="submitRouters">{{ routersModal.saving ? '保存中...' : '保存' }}</CButton>
        </CButtonGroup>
      </template>
    </CModal>

    <!-- 删除确认 Modal -->
    <CModal :show.sync="deleteModal.show" title="删除地域" color="danger">
      确定删除地域「{{ deleteModal.item ? deleteModal.item.name : '' }}」？<br />
      <small class="text-muted">地域下有路由器时不允许删除。</small>
      <template #footer>
        <CButtonGroup>
          <CButton color="secondary" @click="deleteModal.show = false">取消</CButton>
          <CButton color="danger" :disabled="deleteModal.loading" @click="submitDelete">{{ deleteModal.loading ? '删除中...' : '确认删除' }}</CButton>
        </CButtonGroup>
      </template>
    </CModal>
  </div>
</template>

<script>
export default {
  name: 'RegionList',
  data() {
    return {
      loading: false,
      regions: [],
      fields: [
        { key: 'region_id', label: '编号' },
        { key: 'name', label: '名称' },
        { key: 'code', label: '代码' },
        { key: 'remark', label: '备注' },
        { key: 'router_count', label: '路由器' },
        { key: 'actions', label: '操作' }
      ],
      modal: {
        show: false,
        isEdit: false,
        editId: null,
        loading: false,
        form: { name: '', region_id: '', code: '', remark: '' }
      },
      modalValidated: false,
      deleteModal: {
        show: false,
        loading: false,
        item: null
      },
      routersModal: {
        show: false,
        loading: false,
        saving: false,
        region: null,
        list: [],
        selected: []
      }
    }
  },
  mounted() {
    this.loadList()
  },
  methods: {
    async loadList() {
      this.loading = true
      try {
        const res = await this.$axios.get('user/region/list')
        if (res.data.code === 200) {
          this.regions = res.data.data || []
        } else {
          this.$toasted.show(res.data.msg || '加载失败', { type: 'error' })
        }
      } catch (e) {
        this.$toasted.show('网络错误: ' + e.message, { type: 'error' })
      }
      this.loading = false
    },
    openAddModal() {
      this.modalValidated = false
      this.modal.isEdit = false
      this.modal.editId = null
      this.modal.form = { name: '', region_id: String(this.nextRegionId()), code: '', remark: '' }
      this.modal.show = true
    },
    nextRegionId() {
      let max = 0
      this.regions.forEach(r => { if (r.region_id > max) max = r.region_id })
      return max + 1
    },
    openEditModal(item) {
      this.modalValidated = false
      this.modal.isEdit = true
      this.modal.editId = item.id
      this.modal.form = {
        name: item.name || '',
        region_id: item.region_id !== null && item.region_id !== undefined ? String(item.region_id) : '',
        code: item.code || '',
        remark: item.remark || ''
      }
      this.modal.show = true
    },
    async submit() {
      this.modalValidated = true
      const f = this.modal.form
      if (!f.name || !/^\d+$/.test(f.region_id)) {
        return
      }
      this.modal.loading = true
      try {
        const url = this.modal.isEdit ? `user/region/${this.modal.editId}/edit` : 'user/region/add'
        const res = await this.$axios.post(url, f)
        if (res.data.code === 200) {
          this.$toasted.show('已保存', { type: 'success' })
          this.modal.show = false
          this.loadList()
        } else {
          this.$toasted.show(res.data.msg || '保存失败', { type: 'error' })
        }
      } catch (e) {
        this.$toasted.show('网络错误: ' + e.message, { type: 'error' })
      }
      this.modal.loading = false
    },
    confirmDelete(item) {
      this.deleteModal.item = item
      this.deleteModal.show = true
    },
    regionName(regionId) {
      const g = this.regions.find(x => x.id === regionId)
      return g ? g.name : null
    },
    async openRoutersModal(item) {
      this.routersModal.region = item
      this.routersModal.show = true
      this.routersModal.loading = true
      this.routersModal.list = []
      this.routersModal.selected = []
      try {
        const res = await this.$axios.get(`user/region/${item.id}/routers`)
        if (res.data.code === 200) {
          this.routersModal.list = res.data.data || []
          this.routersModal.selected = this.routersModal.list.filter(r => r.in_this_region).map(r => r.id)
        } else {
          this.$toasted.show(res.data.msg || '加载失败', { type: 'error' })
        }
      } catch (e) {
        this.$toasted.show('网络错误: ' + e.message, { type: 'error' })
      }
      this.routersModal.loading = false
    },
    async submitRouters() {
      if (!this.routersModal.region) return
      this.routersModal.saving = true
      try {
        const res = await this.$axios.post(`user/region/${this.routersModal.region.id}/routers`, {
          router_ids: this.routersModal.selected
        })
        if (res.data.code === 200) {
          this.$toasted.show('已保存', { type: 'success' })
          this.routersModal.show = false
          this.loadList()
        } else {
          this.$toasted.show(res.data.msg || '保存失败', { type: 'error' })
        }
      } catch (e) {
        this.$toasted.show('网络错误: ' + e.message, { type: 'error' })
      }
      this.routersModal.saving = false
    },
    async submitDelete() {
      if (!this.deleteModal.item) return
      this.deleteModal.loading = true
      try {
        const res = await this.$axios.delete(`user/region/${this.deleteModal.item.id}/delete`)
        if (res.data.code === 200) {
          this.$toasted.show('已删除', { type: 'success' })
          this.deleteModal.show = false
          this.loadList()
        } else {
          this.$toasted.show(res.data.msg || '删除失败', { type: 'error' })
        }
      } catch (e) {
        this.$toasted.show('网络错误: ' + e.message, { type: 'error' })
      }
      this.deleteModal.loading = false
    }
  }
}
</script>
